<template>
  <BaseListRow surface="grey" padding="none" hoverable class="tw-flex tw-flex-col">
    <div v-if="showRequiredInfoBanner" class="tw-bg-orange-50 tw-px-4 tw-py-2 tw-rounded-t">
      <span class="tw-text-orange-500 tw-font-semibold tw-text-sm">
        {{ $t('Required client onboarding pending') }}
      </span>
    </div>
    <WdsStack align-center justify-space-between gap-xs class="tw-p-4 tw-w-full">
      <WdsStack gap-md direction-row>
        <WdsStack
          class="tw-w-8 tw-h-8 tw-bg-indigo-50 tw-rounded tw-shrink-0"
          justify-center
          align-center
        >
          <WdsIcon :name="WdsIconNameEnum.ENTERPRISE" color="#5d36f1" :size="21" />
        </WdsStack>
        <WdsStack direction-column>
          <WdsText v-if="client.status === ContactStatus.Active" secondary>
            <WdsStack gap-sm>
              <WdsLabel success subtle sm>
                {{ $t('Active') }}
              </WdsLabel>
            </WdsStack>
          </WdsText>
          <WdsText v-if="client.status === ContactStatus.Invited" secondary>
            <WdsStack gap-sm>
              <WdsLabel primary subtle sm>
                {{ $t('New invitation') }}
              </WdsLabel>
            </WdsStack>
          </WdsText>

          <WdsText :text="client.company.name" class="tw-font-bold" />

          <WdsText v-if="!client.recruiterManagesWorkers" small secondary>
            <template v-if="additiveFeesText">
              <span>{{ additiveFeesText }} {{ $t('fee') }}</span>
              <span class="tw-mx-1">&middot;</span>
            </template>
            <span>{{ client.recruiterOwnershipDays ?? '?' }} {{ $t('ownership days') }}</span>
          </WdsText>
        </WdsStack>
      </WdsStack>

      <WdsStack align-center gap-xs>
        <WdsButton
          v-if="client.status === ContactStatus.Invited && client.token"
          primary
          :href="laravelNamedRoute('recruiters.join', { id: client.id })"
          :data-test="`accept-company-invitation-button-${client.company.id}`"
        >
          {{ $t('Accept') }}
        </WdsButton>
        <WdsButton
          v-else-if="showRequiredInfoBanner"
          primary
          :href="laravelNamedRoute(namedRoutes.frontend.clients.onboarding)"
          :data-test="`onboarding-client-button`"
        >
          {{ $t('Complete info') }}
        </WdsButton>
        <WdsMenu z-index="50" placement="bottom-end">
          <template #trigger>
            <WdsButtonGhost>
              <WdsIcon name="overflow-menu-vertical" :size="16" />
            </WdsButtonGhost>
          </template>

          <WdsMenuOption
            v-if="showRequiredInfoBanner"
            :href="laravelNamedRoute(namedRoutes.frontend.clients.onboarding)"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.EDIT" />
            {{ $t('Edit information') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="client.status === ContactStatus.Invited && client.token"
            class="swal-delete faded"
            :data-test="`decline-company-invitation-button-${client.company.id}`"
            :data-swaltext="
              $t('Are you sure you want to decline this invitation from :client?', {
                client: client.company.name,
              })
            "
            :data-swalconfirmtext="$t('Yes, decline')"
            :data-formid="`decline-client-${hashSum(client.id)}`"
            theme="DESTRUCTIVE"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
            {{ $t('Decline invitation') }}
          </WdsMenuOption>

          <WdsTooltip
            v-if="client.status === ContactStatus.Active && client.hasHires"
            global
            :text="$t('You can\'t leave while there is an active hire with the client')"
          >
            <WdsMenuOption disabled theme="DESTRUCTIVE">
              <WdsIcon :size="16" :name="WdsIconNameEnum.EXIT" />
              {{ $t('Leave client') }}
            </WdsMenuOption>
          </WdsTooltip>
          <WdsMenuOption
            v-else-if="client.status === ContactStatus.Active"
            class="swal-delete faded"
            :data-swaltext="
              $t('Are you sure you want to leave :client?', { client: client.company.name })
            "
            :data-swalconfirmtext="$t('Yes, leave')"
            :data-formid="`leave-client-${hashSum(client.id)}`"
            theme="DESTRUCTIVE"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.EXIT" />
            {{ $t('Leave client') }}
          </WdsMenuOption>
        </WdsMenu>
      </WdsStack>

      <teleport to="body">
        <BaseHtmlForm
          v-if="client.status === ContactStatus.Invited && client.token"
          :id="`decline-client-${hashSum(client.id)}`"
          :action="laravelNamedRoute('recruiters.decline', { id: client.id, token: client.token })"
        />
        <BaseHtmlForm
          v-if="client.status === ContactStatus.Active"
          :id="`leave-client-${hashSum(client.id)}`"
          method="delete"
          :action="laravelNamedRoute('recruiters.destroy', { id: client.id })"
        >
          <input type="hidden" name="redirect_uri" :value="href" />
        </BaseHtmlForm>
      </teleport>
    </WdsStack>
  </BaseListRow>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed } from 'vue'
import hashSum from 'hash-sum'
import {
  ContactStatus,
  FeeApplication,
  RecruiterOnboardingStatus,
  type StaffingAgencyClientFragment,
} from '@/gql/graphql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import BaseListRow from '@/components/BaseListRow.vue'

const { $t } = useTranslation()

const staffingAgencyClientFragment = graphql(`
  fragment StaffingAgencyClient on CompanyRecruiter {
    id
    status
    token
    fees {
      id
      application
      formattedRate
    }
    recruiterOwnershipDays
    recruiterManagesWorkers
    hasHires
    requiresOnboarding
    requiredCustomFieldsComplete
    onboardingStatus
    onboardingDocuments {
      id
      isAccepted
    }
    customFieldValues {
      id
    }
    company {
      id
      name
      avatar
    }
  }
`)

const props = defineProps<{
  client: FragmentType<typeof staffingAgencyClientFragment>
}>()

const { href } = document.location
const client = computed(() => useFragment(staffingAgencyClientFragment, props.client))
const additiveFeesText = computed(() =>
  client.value.fees
    .filter((fee) => fee.application === FeeApplication.Additive)
    .map((fee) => fee.formattedRate)
    .join(' + '),
)

const hasRequirements = (c: StaffingAgencyClientFragment): boolean =>
  c.requiresOnboarding &&
  (c.onboardingDocuments.length > 0 ||
    c.customFieldValues.length > 0 ||
    !c.requiredCustomFieldsComplete)

const requirementsComplete = (c: StaffingAgencyClientFragment): boolean => {
  const docsComplete = c.onboardingDocuments.every((doc) => doc.isAccepted)
  const onboardingOk =
    c.onboardingStatus === null || c.onboardingStatus === RecruiterOnboardingStatus.Completed
  return docsComplete && onboardingOk && c.requiredCustomFieldsComplete
}

const showRequiredInfoBanner = computed(
  () =>
    client.value.status === ContactStatus.Active &&
    hasRequirements(client.value) &&
    !requirementsComplete(client.value),
)
</script>

